import { useEffect, useRef, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { MapView } from './map-view.jsx';

/**
 * Pick a single point on the map. Wraps <MapView> in picker mode and adds
 * mapbox-gl-draw configured for point-only drawing. Click on the map drops
 * a marker; clicking again replaces the previous one. "Apply" returns
 * { lat, lon } to the parent.
 *
 * Usage:
 *   <CoordinatePickerModal
 *     open={open}
 *     initial={{ lat: 48.155, lon: 17.107 }}     // optional preset
 *     title="Node location"
 *     onClose={() => setOpen(false)}
 *     onPick={(lat, lon) => setForm({ lat, lon })}
 *   />
 */

const DRAW_CSS_ID  = 'mapbox-gl-draw-css';
const DRAW_CSS_URL = '/runtime-assets/vendor/maplibre/mapbox-gl-draw@1.5.0/mapbox-gl-draw.css';
const DRAW_SCRIPT_ID  = 'mapbox-gl-draw-script';
const DRAW_SCRIPT_URL = '/runtime-assets/vendor/maplibre/mapbox-gl-draw@1.5.0/mapbox-gl-draw.js';

/** Load mapbox-gl-draw vendor JS + CSS. Vendor file references global
 *  `mapboxgl`, so we alias `window.mapboxgl = window.maplibregl` first. */
function loadMapboxDraw() {
  return new Promise((resolve, reject) => {
    if (!document.getElementById(DRAW_CSS_ID)) {
      const l = document.createElement('link');
      l.id = DRAW_CSS_ID; l.rel = 'stylesheet'; l.href = DRAW_CSS_URL;
      document.head.appendChild(l);
    }
    if (!window.mapboxgl && window.maplibregl) {
      // mapbox-gl-draw expects `mapboxgl` global; aliasing is the canonical
      // workaround when running it on top of MapLibre.
      window.mapboxgl = window.maplibregl;
    }
    if (window.MapboxDraw) { resolve(window.MapboxDraw); return; }
    const existing = document.getElementById(DRAW_SCRIPT_ID);
    const finish = () => {
      if (window.MapboxDraw) resolve(window.MapboxDraw);
      else reject(new Error('mapbox-gl-draw failed to initialize'));
    };
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener('error',
        () => reject(new Error('Failed to load mapbox-gl-draw')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.id = DRAW_SCRIPT_ID; s.src = DRAW_SCRIPT_URL; s.async = true;
    s.onload = finish;
    s.onerror = () => reject(new Error('Failed to load mapbox-gl-draw'));
    document.body.appendChild(s);
  });
}

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toFixed(6);
}

export function CoordinatePickerModal({ open, initial, title = 'Pick a location', onClose, onPick }) {
  const [coords, setCoords] = useState(null);   // { lat, lon } | null
  const [error, setError] = useState(null);
  const drawRef = useRef(null);
  const mapRef = useRef(null);

  // Reset state on open/close.
  useEffect(() => {
    if (!open) {
      setCoords(null);
      setError(null);
      drawRef.current = null;
      mapRef.current = null;
      return;
    }
    if (initial && Number.isFinite(initial.lat) && Number.isFinite(initial.lon)) {
      setCoords({ lat: Number(initial.lat), lon: Number(initial.lon) });
    } else {
      setCoords(null);
    }
  }, [open, initial]);

  async function handleMapReady(map) {
    mapRef.current = map;
    try {
      const MapboxDraw = await loadMapboxDraw();
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {},                            // no toolbar — we drive the mode programmatically
        defaultMode: 'simple_select',
      });
      map.addControl(draw, 'top-left');
      drawRef.current = draw;

      // If we got an initial coord, fly there and drop the active marker.
      // Two timing considerations:
      //   1. MapboxDraw registers its render layers in addControl() but the
      //      first paint cycle hasn't necessarily run — feature is in the
      //      store but not yet visible. Wait for `idle` so the map has
      //      fully rendered before we draw.add.
      //   2. If we flyTo first, the camera animation triggers style updates
      //      that can race with draw.add. So flyTo first WITH animate:false
      //      to settle the camera instantly, then add.
      if (initial && Number.isFinite(initial.lat) && Number.isFinite(initial.lon)) {
        map.jumpTo({
          center: [initial.lon, initial.lat],
          zoom: Math.max(map.getZoom(), 10),
        });
        const placeMarker = () => {
          // Modal may have closed between addControl and the next idle —
          // useEffect cleanup nulled drawRef.current in that case.
          if (drawRef.current !== draw) return;
          try {
            const ids = draw.add({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [initial.lon, initial.lat] },
              properties: {},
            });
            const id = Array.isArray(ids) ? ids[0] : null;
            drawRef.current._activeId = id;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('draw.add failed', e);
          }
        };
        if (map.loaded() && map.isStyleLoaded()) {
          // Defer one frame so MapboxDraw layers commit before we add.
          requestAnimationFrame(placeMarker);
        } else {
          map.once('idle', placeMarker);
        }
      } else {
        draw.changeMode('draw_point');
      }

      map.on('draw.create', (e) => {
        const newFeat = e.features[0];
        if (!newFeat) return;
        // Keep only this point — delete any siblings.
        const all = draw.getAll();
        const others = all.features.filter((f) => f.id !== newFeat.id).map((f) => f.id);
        if (others.length) draw.delete(others);
        drawRef.current._activeId = newFeat.id;

        const [lon, lat] = newFeat.geometry.coordinates;
        setCoords({ lat, lon });
        // Stay in simple_select after creating; user can hit "Pick again" to re-enter.
      });

      // Editing the marker by dragging it.
      map.on('draw.update', (e) => {
        const f = e.features[0];
        if (!f) return;
        const [lon, lat] = f.geometry.coordinates;
        setCoords({ lat, lon });
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  function handlePickAgain() {
    const draw = drawRef.current;
    if (!draw) return;
    try {
      draw.deleteAll();
      drawRef.current._activeId = null;
      setCoords(null);
      draw.changeMode('draw_point');
    } catch { /* ignore */ }
  }

  function handleApply() {
    if (!coords) return;
    onPick?.(coords.lat, coords.lon);
    onClose?.();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={
        coords
          ? 'A marker is placed. Drag it to fine-tune, click "Pick again" to replace, or "Apply" to use these coordinates.'
          : 'Click anywhere on the map to drop a marker.'
      }
      size="xl"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePickAgain}
            disabled={!coords}
            className="btn-secondary"
          >
            <i className="fa-solid fa-rotate" aria-hidden="true" />
            Pick again
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!coords}
            className="btn-primary"
          >
            <i className="fa-solid fa-check" aria-hidden="true" />
            Apply
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        {/* Coordinate readout */}
        <div className="flex items-center gap-4 rounded-2xl border border-line bg-canvas px-4 py-3">
          <i className="fa-solid fa-location-crosshairs text-accent" aria-hidden="true" />
          <div className="flex flex-1 items-center gap-6 font-mono text-[13px]">
            <span>
              <span className="text-[10px] uppercase tracking-wider text-mist">LAT</span>{' '}
              <span className={coords ? 'font-semibold text-ink' : 'text-mist'}>{fmt(coords?.lat)}</span>
            </span>
            <span>
              <span className="text-[10px] uppercase tracking-wider text-mist">LON</span>{' '}
              <span className={coords ? 'font-semibold text-ink' : 'text-mist'}>{fmt(coords?.lon)}</span>
            </span>
          </div>
        </div>

        {/* Map */}
        <div
          className="overflow-hidden rounded-2xl border border-line bg-canvas"
          style={{ height: '560px', position: 'relative' }}
        >
          <MapView
            mode="picker"
            initialCenter={
              initial && Number.isFinite(initial.lon) && Number.isFinite(initial.lat)
                ? [initial.lon, initial.lat]
                : [30.5234, 50.4501]
            }
            initialZoom={initial ? 10 : 5.5}
            onReady={handleMapReady}
            style={{ position: 'absolute', inset: 0 }}
          />
        </div>
      </div>
    </Modal>
  );
}
